import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { Action } from 'typescript-fsa';
import { change, getFormValues } from 'redux-form';

import { hideLoader, showLoader } from '@topconagriculture/loader';
import { FormName } from '@topconagriculture/feed-types';

import api from '../../../core/api';
import * as actions from './groupPreview.actions';
import { previewEditFeedGroup } from './groupPreview.actions';
import { composeErrorMessage, transformFormValueToNumber } from '../../../core/helpers/utils/utils';
import { Group } from '../../entities/group/group.types';
import { LoadersNames } from '../../ui/ui.constants';
import { notificationError } from '../../../core/helpers/utils/notificationUtils';
import { Forms } from '../../form/form.selector';
import { GroupFormValue } from '../../../routes/FeedGroups/Groups/shared/GroupForm/GroupPartialForm';
import { farmsSelector, feedingsSelector, recipesSelector, selectedSelector } from '../../selectors';
import {
  formatDraggableFeedings,
  formatGroupValues,
} from '../../../routes/FeedGroups/Groups/shared/GroupForm/formUtils';
import { getIsEditMode } from './groupPreview.selector';
import { ChangedKeys, PreviewBunkReaderScorePayload } from './groupPreview.types';
import { compose403ErrorMessage, successHandler } from '../../../core/helpers/utils/sagaUtils';
import { getSelectedBunkReaderPen, getSelectedBunkReaderPenId } from '../../selected/selected.selector';
import { getBunkReaderScoreSettings } from '../../entities/settings/settings.selector';
import { BunkReaderAdjustmentType } from '../../entities/settings/settings.types';

export function* previewCreateGroupSaga(action: Action<any>) {
  yield put(showLoader(LoadersNames.fetchFeedingGroupTotals));

  const formValues: GroupFormValue = yield select(getFormValues(Forms.groupCreationForm));
  const feedingPeriods = yield select(feedingsSelector.getFeedingPeriodList);
  const recipes = yield select(recipesSelector.getRecipes);
  const isBeef: boolean = yield select(farmsSelector.isCurrentFarmBeef);
  const selectedGroup = yield select(selectedSelector.getSelectedGroup);
  const isEditMode = yield select(getIsEditMode);

  let changedKey;

  switch (action.type) {
    case actions.updateFeedingFactor.type:
      changedKey = ChangedKeys.FEEDING_FACTOR;
      break;
    case actions.updateLoadWeight.type:
    case actions.lockFeeding.type:
      changedKey = ChangedKeys.LOAD_WEIGHT;
      break;
    case actions.updateLoadWeightDry.type:
      changedKey = ChangedKeys.LOAD_WEIGHT_DRY;
      break;
    case actions.updateGlobalFeedFactor.type:
      changedKey = ChangedKeys.GLOBAL_FEEDING_FACTOR;
      break;
    case actions.updateHeadCount.type:
      changedKey = ChangedKeys.HEAD_COUNT;
      break;
    default:
      changedKey = ChangedKeys.OTHER;
      break;
  }

  const group = {
    ...formatGroupValues({ ...formValues }, isBeef, changedKey),
    id: selectedGroup?.id,
  } as Group;
  try {
    const result = yield call(isEditMode ? api.group.simulateEditGroup : api.group.simulateCreateGroup, group);
    const draggableIds = formValues?.feedings
      .map((formRow) => formRow.items.map((item) => item.draggableId))
      .reduce((acc, val) => {
        acc.push(...val);
        return acc;
      }, []);
    const feedings = formatDraggableFeedings(feedingPeriods, result.feedings, recipes, draggableIds);
    yield put(
      previewEditFeedGroup.done({
        params: {},
        result,
      }),
    );
    yield put(change(Forms.groupCreationForm, 'feedings', feedings));
  } catch (err) {
    if (err.status === 409 || err.status === 400) {
      const draggableIds = formValues?.feedings
        .map((formRow) => formRow.items.map((item) => item.draggableId))
        .reduce((acc, val) => {
          acc.push(...val);
          return acc;
        }, []);
      const feedings = formatDraggableFeedings(feedingPeriods, err.data.feedings, recipes, draggableIds);
      yield put(change(Forms.groupCreationForm, 'feedings', feedings));
      yield put(
        previewEditFeedGroup.done({
          params: {},
          result: err.data,
        }),
      );
    }
    if (err.status === 403) {
      yield compose403ErrorMessage(err);
    } else {
      yield put(notificationError(composeErrorMessage(err)));
    }
  }
  yield put(hideLoader(LoadersNames.fetchFeedingGroupTotals));
}

const calculatepayload = ({ brAdjustmentType, bunkScoreGrades, bunkReader, formValues, brDefaultscore }) => {
  const targetBunkScoreGrade = bunkScoreGrades.find(
    ({ score }) => score === transformFormValueToNumber(formValues.brDefaultscore),
  );
  const payload: PreviewBunkReaderScorePayload = {};

  switch (brAdjustmentType) {
    case BunkReaderAdjustmentType.DAILY_REQUIREMENT:
      const newPercentage = targetBunkScoreGrade.percentage;

      const newGlobalFeedingFactor = bunkReader.globalFeedingFactor + newPercentage;
      payload.globalFeedingFactor = newGlobalFeedingFactor;
      break;

    case BunkReaderAdjustmentType.ACTUAL_WEIGHT:
      const newWeightScore = targetBunkScoreGrade.weight;

      const newActualWeightTotal = bunkReader.actualWeightTotal + newWeightScore;
      payload.actualWeightTotal = newActualWeightTotal;
      break;
    default:
      break;
  }

  return payload;
};

function* previewEditBunkScoresSaga(action: Action<{ type: string }>) {
  try {
    const formValues = yield select(getFormValues(FormName.bunkreader));
    const groupId = yield select(getSelectedBunkReaderPenId);

    let payload: PreviewBunkReaderScorePayload = {};

    switch (action.type) {
      case actions.setGradeId.type:
        const { brAdjustmentType, bunkScoreGrades, brDefaultscore } = yield select(getBunkReaderScoreSettings);
        const bunkReader = yield select(getSelectedBunkReaderPen);
        payload = calculatepayload({ brAdjustmentType, bunkScoreGrades, bunkReader, formValues, brDefaultscore });
        break;
      case actions.setResultPercentage.type:
        payload.globalFeedingFactor = transformFormValueToNumber(formValues.globalFeedingFactor);
        break;
      case actions.setResultWeight.type:
        payload.actualWeightTotal = transformFormValueToNumber(formValues.actualWeightTotal);
        break;
      default:
        break;
    }

    const result = yield call(api.group.simulateEditGroupBunkScore, groupId, payload);
    successHandler(action.meta, result);
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
}

function* getAllBunkReaderNotes(action: ReturnType<typeof actions.getAllBunkReaderNotes>) {
  try {
    const groupId = yield select(getSelectedBunkReaderPenId);

    const result = yield call(api.group.getBunkReaderNotes, groupId);

    successHandler(action.meta, result);
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
}

export function* watchAppFeedGroupsSaga() {
  yield all([
    takeLatest(
      [
        actions.lockFeeding.type,
        actions.updateLoadWeight.type,
        actions.updateLoadWeightDry.type,
        actions.updateDaysOnRation.type,
        actions.updateFeedingFactor.type,
        actions.updateHeadCount.type,
        actions.updateGlobalFeedFactor.type,
        actions.updateFeedings.type,
      ],
      previewCreateGroupSaga,
    ),
    takeLatest(
      [actions.setGradeId.type, actions.setResultPercentage.type, actions.setResultWeight.type],
      previewEditBunkScoresSaga,
    ),
    takeLatest(actions.getAllBunkReaderNotes.type, getAllBunkReaderNotes),
  ]);
}
